import { Volume2, Wifi, Send, Heart } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import useNowPlaying from "@/hooks/useNowPlaying";
import ShareCard from "@/components/ShareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// === STATION CONTACT CONFIG ===
// PayPal Hosted Button ID — single "Pay Now" form action.
const PAYPAL_HOSTED_BUTTON_ID = "RPQW3YEHV3U2J";

/**
 * =========================================================================
 *  RADIO PLAYER EMBED
 *  ───────────────────────────────────────────────────────────────────────
 *  Paste your external radio player code inside the <div id="player-embed">
 *  block below. Examples:
 *    - <iframe src="https://your-station.com/player" width="100%"
 *        height="200" frameBorder="0" allow="autoplay"></iframe>
 *    - <script src="https://your-station.com/embed.js"></script>
 *  Anything you drop in there replaces the placeholder UI.
 * =========================================================================
 */
export default function RadioPlayer() {
  const np = useNowPlaying();
  const hasTrack = !!(np?.ok && np?.title);
  return (
    <section
      className="relative bg-zinc-950 scanlines"
      data-testid="radio-player-section"
    >
      {/* Top meta bar */}
      <div className="grid grid-cols-12 border-b border-zinc-800 text-xs">
        <div className="col-span-4 px-4 py-3 border-r border-zinc-800 flex items-center gap-2 text-zinc-300 uppercase tracking-widest">
          <span className="on-air-dot" aria-hidden /> ON AIR
        </div>
        <div className="col-span-4 px-4 py-3 border-r border-zinc-800 text-zinc-100 uppercase tracking-widest">
          LIVE STREAM
        </div>
        <div className="col-span-4 px-4 py-3 text-zinc-100 uppercase tracking-widest text-right">
          <Wifi className="inline w-3 h-3 mr-1" /> 320kbps
        </div>
      </div>

      <div className="p-6 md:p-10">
        {/* Station ID block */}
        <div className="mb-8" data-testid="station-id-block">
          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-2">
            //_STATION_ID
          </div>
          <h2
            className="font-display text-5xl md:text-7xl leading-none text-zinc-50 glitch"
            data-testid="station-id-title"
          >
            DIGITAL <span className="text-red-600">REVOLUTION</span> RADIO
          </h2>
          <div className="mt-3 text-sm text-zinc-100 tracking-widest uppercase">
            Hard Rock · Heavy Metal · Broadcasting 24 / 7
          </div>
          {/* Share the current spin — brutalist OG card generator. Only
              renders when a track is actively spinning so no dead UI. */}
          {hasTrack && (
            <div className="mt-5 flex items-center gap-3 flex-wrap" data-testid="station-id-share-row">
              <span className="text-[10px] tracking-[0.35em] text-zinc-500 uppercase font-display">
                //_now_spinning
              </span>
              <span
                className="text-sm text-zinc-100 font-mono tracking-tight truncate max-w-md"
                title={np.artist ? `${np.artist} — ${np.title}` : np.title}
              >
                {np.artist ? `${np.artist} — ${np.title}` : np.title}
              </span>
              <ShareCard title={np.title} artist={np.artist} />
            </div>
          )}
        </div>

        {/* Player on TOP — Request form + Donate UNDERNEATH */}
        <div
          className="border-2 border-zinc-700 bg-black/60"
          data-testid="player-embed-wrapper"
        >
          {/* TOP ROW — LIVE365 PLAYER (front and center) */}
          <div className="border-b border-zinc-700 flex flex-col">
            <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2">
              <span className="text-xs tracking-[0.4em] text-zinc-300 uppercase">
                //_TRANSMITTER_UNIT — LIVE NOW
              </span>
              <span className="text-xs tracking-widest text-red-600 uppercase animate-pulse">
                ● ON AIR
              </span>
            </div>
            <div
              id="player-embed"
              className="flex flex-col md:flex-row items-center justify-center gap-4 p-4 bg-zinc-950/60"
            >
              {/* ↓↓↓ LIVE365 STREAMING PLAYER (with play button) ↓↓↓ */}
              <iframe
                title="Digital Revolution Radio Live Player"
                width="450"
                height="511"
                frameBorder="0"
                src="https://live365.com/embeds/v1/player/a05268?s=md&m=dark&autoplay=1&a=1"
                allow="autoplay; encrypted-media"
                data-testid="live365-player-iframe"
                className="w-full max-w-[450px] h-[511px] border border-zinc-800"
              ></iframe>
              {/* ↓↓↓ LIVE365 LAST-PLAYED WIDGET (song history with album art) ↓↓↓ */}
              <iframe
                title="Digital Revolution Radio — Last Played"
                width="450"
                height="511"
                frameBorder="0"
                src="https://live365.com/embeds/v1/played/a05268?s=md&m=dark"
                data-testid="live365-lastplayed-iframe"
                className="w-full max-w-[450px] h-[511px] border border-zinc-800"
              ></iframe>
              {/* ↑↑↑ LIVE365 EMBEDS ↑↑↑ */}
            </div>
          </div>

          {/* BOTTOM ROW — Request form (left) + Donate (right) */}
          <div className="grid grid-cols-12 gap-0">
            <div className="col-span-12 lg:col-span-7 border-b lg:border-b-0 lg:border-r border-zinc-700 p-6">
              <RequestForm />
            </div>
            <div className="col-span-12 lg:col-span-5 p-6 flex flex-col gap-4">
              <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase">
                //_SUPPORT_THE_STATION
              </div>
              <div className="font-display text-2xl text-zinc-100 leading-none">
                KEEP US ON AIR
              </div>
              <div className="text-sm text-zinc-300 leading-relaxed">
                Digital Revolution Radio runs on caffeine, distortion, and
                listener support. Drop a few bucks in the tip jar so we can
                keep the signal hot.
              </div>
              <DonateButton />
              <div className="mt-auto pt-4 border-t border-zinc-800 flex items-center gap-3">
                <span className="font-display text-base text-zinc-300 tracking-tight">
                  DIGITAL REVOLUTION RADIO
                </span>
                <span className="text-xs text-zinc-600 tracking-widest uppercase flex items-center gap-1">
                  <Volume2 className="w-3 h-3" /> Crank it.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlaceholderPlayer() {
  return (
    <div className="w-full" data-testid="player-placeholder">
      <div className="flex items-center gap-4">
        <button
          type="button"
          aria-label="Play"
          className="w-16 h-16 bg-red-600 text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors border-2 border-zinc-700"
          data-testid="placeholder-play-btn"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
        <div className="flex-1">
          <div className="text-xs tracking-[0.3em] text-zinc-300 uppercase mb-2">
            Player embed not configured
          </div>
          <div className="font-display text-xl text-zinc-200 leading-tight">
            DROP YOUR PLAYER CODE HERE
          </div>
          <div className="text-xs text-zinc-300 mt-1 font-mono">
            src/components/RadioPlayer.jsx — find{" "}
            <span className="text-red-500">&lt;div id="player-embed"&gt;</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequestForm() {
  const [name, setName] = useState("");
  const [song, setSong] = useState("");
  const [show, setShow] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showOptions, setShowOptions] = useState([]);

  // Pull the unique list of show names from the backend schedule so the
  // dropdown stays in sync with whatever is on /api/schedule. Dedupe by name
  // (a show that airs multiple days only appears once).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/schedule`);
        const days = res.data?.schedule || {};
        const seen = new Set();
        const names = [];
        for (const dayShows of Object.values(days)) {
          for (const s of dayShows || []) {
            const n = (s.name || "").trim();
            if (n && !seen.has(n.toLowerCase())) {
              seen.add(n.toLowerCase());
              names.push(n);
            }
          }
        }
        names.sort((a, b) => a.localeCompare(b));
        if (!cancelled) setShowOptions(names);
      } catch (e) {
        /* silent — dropdown just shows "Any Show / Not Sure" */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!song.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/song-request`, {
        name: name.trim(),
        song: song.trim(),
        show: show.trim(),
        note: note.trim(),
      });
      toast.success("Request sent — we'll spin it loud 🤘");
      setName("");
      setSong("");
      setShow("");
      setNote("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send request — try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="request-form"
      className="flex flex-col"
    >
      <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-2">
        //_REQUEST_A_TRACK
      </div>
      <div className="font-display text-2xl md:text-3xl text-zinc-100 leading-none mb-1">
        SHOUT IT OUT.
      </div>
      <div className="text-xs text-zinc-300 mb-5 leading-relaxed">
        Tell us what to spin. Goes straight to the booth — no email app needed.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-700">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="YOUR NAME (OPTIONAL)"
          maxLength={60}
          className="bg-zinc-950 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:bg-black tracking-wide font-mono border-b md:border-b-0 md:border-r border-zinc-700"
          data-testid="request-name-input"
        />
        <input
          type="text"
          value={song}
          onChange={(e) => setSong(e.target.value)}
          placeholder="SONG / ARTIST"
          required
          maxLength={120}
          className="bg-zinc-950 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:bg-black tracking-wide font-mono"
          data-testid="request-song-input"
        />
      </div>
      <select
        value={show}
        onChange={(e) => setShow(e.target.value)}
        className="border border-t-0 border-zinc-700 bg-zinc-950 px-3 py-3 text-sm text-zinc-100 outline-none focus:bg-black tracking-wide font-mono uppercase"
        data-testid="request-show-select"
      >
        <option value="">ANY SHOW / NOT SURE</option>
        {showOptions.map((s) => (
          <option key={s} value={s}>
            {s.toUpperCase()}
          </option>
        ))}
      </select>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={300}
        placeholder="SHOUT-OUT / DEDICATION (OPTIONAL)"
        className="border border-t-0 border-zinc-700 bg-zinc-950 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:bg-black tracking-wide font-mono resize-none"
        data-testid="request-note-input"
      />
      <button
        type="submit"
        disabled={!song.trim() || submitting}
        className="mt-3 self-start inline-flex items-center gap-2 bg-red-600 hover:bg-white hover:text-black disabled:opacity-40 disabled:cursor-not-allowed text-white font-display tracking-widest uppercase px-6 py-3 transition-colors border border-zinc-700"
        data-testid="request-send-btn"
      >
        <Send className="w-4 h-4" /> {submitting ? "Sending..." : "Send Request"}
      </button>
      <p className="text-[10px] text-zinc-600 tracking-widest uppercase mt-2">
        //_delivered_instantly · no_signup_required
      </p>
    </form>
  );
}

function DonateButton() {
  return (
    <div data-testid="donate-paypal-wrapper" className="space-y-2">
      <div className="font-display text-base text-zinc-200 tracking-tight uppercase flex items-center gap-2">
        <Heart className="w-4 h-4 text-red-500" />
        Donate via PayPal
      </div>
      <style>{`
        .pp-${PAYPAL_HOSTED_BUTTON_ID} {
          text-align: center;
          border: none;
          border-radius: 0.25rem;
          min-width: 11.625rem;
          padding: 0 2rem;
          height: 2.625rem;
          font-weight: bold;
          background-color: #FFD140;
          color: #000000;
          font-family: "Helvetica Neue", Arial, sans-serif;
          font-size: 1rem;
          line-height: 1.25rem;
          cursor: pointer;
          transition: filter 0.15s ease;
        }
        .pp-${PAYPAL_HOSTED_BUTTON_ID}:hover { filter: brightness(1.08); }
      `}</style>
      <form
        action={`https://www.paypal.com/ncp/payment/${PAYPAL_HOSTED_BUTTON_ID}`}
        method="post"
        target="_blank"
        style={{
          display: "inline-grid",
          justifyItems: "start",
          alignContent: "start",
          gap: "0.5rem",
        }}
        data-testid="paypal-donate-form"
      >
        <input
          className={`pp-${PAYPAL_HOSTED_BUTTON_ID}`}
          type="submit"
          value="Pay Now"
          data-testid="paypal-pay-now-btn"
        />
        <img
          src="https://www.paypalobjects.com/images/Debit_Credit_APM.svg"
          alt="cards"
          className="opacity-90"
        />
        <section style={{ fontSize: "0.75rem", color: "#a1a1aa" }}>
          Powered by{" "}
          <img
            src="https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg"
            alt="paypal"
            style={{ height: "0.875rem", verticalAlign: "middle" }}
          />
        </section>
      </form>
    </div>
  );
}
